import React, { FC, useState, useCallback, useEffect, useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Accordion } from 'Components/Accordion';
import { ProductAnalyticsContext } from 'Context/ProductAnalytics';
import withErrorBoundary from 'HOC/ErrorBoundary';
import RelativeEngagement from './RelativeEngagement';
import DateRegisteredWithMyInstitution from './DateRegisteredWithMyInstitution';
import ActivityWithEab from './ActivityWithEab';
import ActivityWithMyInstitution from './ActivityWithMyInstitution';
import AppliedToMyInstitution from './AppliedToMyInstitution';
import ScheduleVisitWithMyInstitution from './ScheduleVisitWithMyInstitution';
import TotalTimeSpentMyVtIwc from './TotalTimeSpentMyVtIwc';
import {
  setDateRegisteredValue,
  setRelativeEngagementValue,
  clearRelativeEngagement,
  selectAllRelativeEngagement,
  setTotalTimeSpentValue,
  setScheduledVisitValue,
  setAppliedValue,
  setActivityWithInstitutionValue,
  setActivityWithEABValue,
} from './actions/engagement.actions';

const Engagement: FC = () => {
  const {
    dateRegistered,
    relativeEngagement,
    totalTimeSpent,
    activityWithInstitution,
    activityWithEAB,
    applied,
    scheduledVisit,
  } = useSelector((selectors: any) => selectors.filter.engagement);

  const {
    dateRegisteredQuery,
    relativeEngagementQuery,
    totalTimeSpentQuery,
    scheduledVisitQuery,
    appliedQuery,
    activityWithInstitutionQuery,
    activityWithEABQuery,
  } = useSelector((selectors: any) => selectors.filter.query);
  const dispatch = useDispatch();

  const {
    filterTrackingHelpers: { Engagement: EngagementTrackingHelper },
    actions: { filterTrack },
  } = useContext(ProductAnalyticsContext);

  const dateRegisteredCallback = (event: any) => {
    const { target } = event;

    // Combined Analytics Event
    filterTrack(EngagementTrackingHelper.dateRegistered(target));

    setDateRegisteredValue(dispatch, dateRegistered, target.value, target.id);
  };

  const relativeEngagementCallback = (event: any) => {
    const { value, id } = event.target;

    // Combined Analytics Event
    filterTrack({
      filterCategory: 'Engagement',
      filterName: `RelativeEngagement.${id}`,
      filterValue: value,
      addToStore: value,
    });

    setRelativeEngagementValue(dispatch, relativeEngagement, id);
  };

  const selectAllRelativeEngagementCallBack = () => {
    // Combined Analytics Event
    filterTrack({
      filterCategory: 'Engagement',
      filterName: 'RelativeEngagement.high',
      filterValue: true,
      addToStore: true,
    });
    filterTrack({
      filterCategory: 'Engagement',
      filterName: 'RelativeEngagement.medium',
      filterValue: true,
      addToStore: true,
    });
    filterTrack({
      filterCategory: 'Engagement',
      filterName: 'RelativeEngagement.low',
      filterValue: true,
      addToStore: true,
    });

    // Select all checkboxes
    selectAllRelativeEngagement(dispatch);
  };
  const clearRelativeEngagementCallBack = () => {
    // Combined Analytics Event
    filterTrack({
      filterCategory: 'Engagement',
      filterName: 'RelativeEngagement.high',
      filterValue: false,
      addToStore: false,
    });
    filterTrack({
      filterCategory: 'Engagement',
      filterName: 'RelativeEngagement.medium',
      filterValue: false,
      addToStore: false,
    });
    filterTrack({
      filterCategory: 'Engagement',
      filterName: 'RelativeEngagement.low',
      filterValue: false,
      addToStore: false,
    });

    clearRelativeEngagement(dispatch);
  };

  const totalTimeSpentCallback = (event: any, identifier: any) => {
    const { value, label } = event.target;

    setTotalTimeSpentValue(dispatch, totalTimeSpent, value, identifier);
    // Combined Analytics event
    filterTrack(EngagementTrackingHelper.totalTimeSpent(label, totalTimeSpent));
  };

  const scheduledVisitCallback = (event: any) => {
    const { value } = event.target;

    // Combined Analytics Event
    filterTrack({
      filterCategory: 'Engagement',
      filterName: 'ScheduledVisit',
      filterValue: value,
      addToStore: value,
    });

    setScheduledVisitValue(dispatch, value);
  };

  const appliedCallback = (event: any) => {
    const { value } = event.target;

    // Combined Analytics Event
    filterTrack({
      filterCategory: 'Engagement',
      filterName: 'AppliedToMyInstitution',
      filterValue: value,
      addToStore: value,
    });

    setAppliedValue(dispatch, value);
  };

  const activityWithInstitutionCallback = (event: any, identifier: any) => {
    // Combined Analytics event
    filterTrack(
      EngagementTrackingHelper.activityWithMyInstitution(event.target)
    );

    setActivityWithInstitutionValue(
      dispatch,
      activityWithInstitution,
      event.target.value,
      identifier
    );
  };

  const activityWithEABCallback = (event: any, identifier: any) => {
    // Combined Analytics event
    filterTrack(EngagementTrackingHelper.activityWithEabNetwork(event.target));

    setActivityWithEABValue(
      dispatch,
      activityWithEAB,
      event.target.value,
      identifier
    );
  };

  // Build general Engagement filter structure here.
  // Wrap all callbacks in a useCallback with their dependency
  // when constructing them.
  const [accDateRegistered, SetAccDateRegistered] = useState(true);
  const [accRelativeEngagement, SetAccRelativeEngagement] = useState(
    relativeEngagementQuery !== ''
  );
  const [accTotalTimeSpent, SetAccTotalTimeSpent] = useState(
    totalTimeSpentQuery !== ''
  );
  const [
    accScheduledVisitWithMyInstitution,
    SetAccScheduledVisitWithMyInstitution,
  ] = useState(scheduledVisitQuery !== '');
  const [accAppliedToMyInstitution, SetAccAppliedToMyInstitution] = useState(
    appliedQuery !== ''
  );
  const [accActivityWithMyInstitution, SetAccActivityWithMyInstitution] =
    useState(activityWithInstitutionQuery !== '');
  const [accActivityWithEABNetwork, SetAccActivityWithEABNetwork] = useState(
    activityWithEABQuery !== ''
  );
  const accTotalTimeSpentCallBack = () => {
    SetAccTotalTimeSpent(!accTotalTimeSpent);
  };
  const accSetAccActivityWithEABNetworkCallBack = () => {
    SetAccActivityWithEABNetwork(!accActivityWithEABNetwork);
  };
  const accSetAccActivityWithMyInstitutionCallBack = () => {
    SetAccActivityWithMyInstitution(!accActivityWithMyInstitution);
  };
  const accSetAccAppliedToMyInstitutionCallBack = () => {
    SetAccAppliedToMyInstitution(!accAppliedToMyInstitution);
  };
  const accSetAccScheduledVisitWithMyInstitutionCallBack = () => {
    SetAccScheduledVisitWithMyInstitution(!accScheduledVisitWithMyInstitution);
  };
  const accSetAccRelativeEngagementCallBack = () => {
    SetAccRelativeEngagement(!accRelativeEngagement);
  };

  useEffect(() => {
    if (dateRegisteredQuery !== '')
      SetAccDateRegistered(dateRegisteredQuery !== '');
  }, [dateRegisteredQuery]);

  useEffect(() => {
    if (relativeEngagementQuery !== '')
      SetAccRelativeEngagement(relativeEngagementQuery !== '');
  }, [relativeEngagementQuery]);

  useEffect(() => {
    if (totalTimeSpentQuery !== '')
      SetAccTotalTimeSpent(totalTimeSpentQuery !== '');
  }, [totalTimeSpentQuery]);

  useEffect(() => {
    if (scheduledVisitQuery !== '')
      SetAccScheduledVisitWithMyInstitution(scheduledVisitQuery !== '');
  }, [scheduledVisitQuery]);

  useEffect(() => {
    if (appliedQuery !== '') SetAccAppliedToMyInstitution(appliedQuery !== '');
  }, [appliedQuery]);

  useEffect(() => {
    if (activityWithInstitutionQuery !== '')
      SetAccActivityWithMyInstitution(activityWithInstitutionQuery !== '');
  }, [activityWithInstitutionQuery]);

  useEffect(() => {
    if (activityWithEABQuery !== '')
      SetAccActivityWithEABNetwork(activityWithEABQuery !== '');
  }, [activityWithEABQuery]);

  return (
    <ul>
      <li>
        <Accordion
          key="dateregisteredwithmyinstituion"
          title="Date Registered with My Institution"
          id="dateregisteredwithmyinstituion"
          isOpen={accDateRegistered}
          accordionCallback={() => {
            SetAccDateRegistered(!accDateRegistered);
          }}
          useTopDivider={false}
          useBottomDivider={false}
          accordionType="Section"
          isActive={dateRegisteredQuery !== ''}
          overrideStylesChildContainer={{
            marginLeft: '17px',
          }}
          color="#079DC0"
        >
          <DateRegisteredWithMyInstitution
            callback={dateRegisteredCallback}
            dateRegistered={dateRegistered}
          />
        </Accordion>
      </li>
      <li>
        <Accordion
          key="relativeengagement"
          title="Relative Engagement"
          id="relativeengagement"
          isOpen={accRelativeEngagement}
          accordionCallback={accSetAccRelativeEngagementCallBack}
          isActive={relativeEngagementQuery !== ''}
          useClearButton={relativeEngagementQuery !== ''} // No query setup for this one yet.
          clear={useCallback(clearRelativeEngagementCallBack, [
            relativeEngagement.high,
            relativeEngagement.medium,
            relativeEngagement.low,
          ])}
          useSelectAllButton={
            !relativeEngagement.high ||
            !relativeEngagement.medium ||
            !relativeEngagement.low
          }
          selectAll={useCallback(selectAllRelativeEngagementCallBack, [
            relativeEngagement.high,
            relativeEngagement.medium,
            relativeEngagement.low,
          ])}
          useTopDivider={false}
          useBottomDivider={false}
          accordionType="Section"
          overrideStylesChildContainer={{
            marginLeft: '23px',
          }}
          color="#079DC0"
        >
          <RelativeEngagement
            callback={relativeEngagementCallback}
            relativeEngagement={relativeEngagement}
          />
        </Accordion>
      </li>
      <li>
        <Accordion
          key="totaltimespent"
          title="Total Time Spent in My VT and/or IWC"
          id="totaltimespent"
          isOpen={accTotalTimeSpent}
          accordionCallback={accTotalTimeSpentCallBack}
          useTopDivider={false}
          useBottomDivider={false}
          accordionType="Section"
          isActive={totalTimeSpentQuery !== ''}
          overrideStylesChildContainer={{
            marginLeft: '17px',
          }}
          color="#079DC0"
        >
          <TotalTimeSpentMyVtIwc
            callback={totalTimeSpentCallback}
            totalTimeSpent={totalTimeSpent}
          />
        </Accordion>
      </li>
      <li>
        <Accordion
          key="scheduledvisitswithmyinstitution"
          title="Schedule Visit CTA Clicked"
          id="scheduledvisitswithmyinstitution"
          isOpen={accScheduledVisitWithMyInstitution}
          accordionCallback={accSetAccScheduledVisitWithMyInstitutionCallBack}
          useTopDivider={false}
          useBottomDivider={false}
          accordionType="Section"
          isActive={scheduledVisitQuery !== ''}
          overrideStylesChildContainer={{
            marginLeft: '17px',
          }}
          color="#079DC0"
        >
          <ScheduleVisitWithMyInstitution
            callback={scheduledVisitCallback}
            scheduledVisit={scheduledVisit}
          />
        </Accordion>
      </li>
      <li>
        <Accordion
          key="appliedtomyinstitution"
          title="Apply Now CTA Clicked"
          id="appliedtomyinstitution"
          isOpen={accAppliedToMyInstitution}
          accordionCallback={accSetAccAppliedToMyInstitutionCallBack}
          useTopDivider={false}
          useBottomDivider={false}
          accordionType="Section"
          isActive={appliedQuery !== ''}
          overrideStylesChildContainer={{
            marginLeft: '17px',
          }}
          color="#079DC0"
        >
          <AppliedToMyInstitution
            callback={appliedCallback}
            applied={applied}
          />
        </Accordion>
      </li>
      <li>
        <Accordion
          key="activitywithmyinstitution"
          title="Activity with My Institution"
          id="activitywithmyinstitution"
          isOpen={accActivityWithMyInstitution}
          accordionCallback={accSetAccActivityWithMyInstitutionCallBack}
          useTopDivider={false}
          useBottomDivider={false}
          accordionType="Section"
          isActive={activityWithInstitutionQuery !== ''}
          overrideStylesChildContainer={{
            marginLeft: '17px',
          }}
          color="#079DC0"
        >
          <ActivityWithMyInstitution
            callback={activityWithInstitutionCallback}
            activityWithInstitution={activityWithInstitution}
          />
        </Accordion>
      </li>
      <li>
        <Accordion
          key="activitywitheab"
          title="Activity with EAB Network"
          id="activitywitheab"
          isOpen={accActivityWithEABNetwork}
          accordionCallback={accSetAccActivityWithEABNetworkCallBack}
          useTopDivider={false}
          useBottomDivider={false}
          accordionType="Section"
          isActive={activityWithEABQuery !== ''}
          overrideStylesChildContainer={{
            marginLeft: '17px',
          }}
          color="#079DC0"
        >
          <ActivityWithEab
            callback={activityWithEABCallback}
            activityWithEAB={activityWithEAB}
          />
        </Accordion>
      </li>
    </ul>
  );
};

export default withErrorBoundary(Engagement);
